<template>
  <component
    :is="tag"
    :type="componentType"
    v-bind="buttonProps"
    class="new-o-button focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    tabindex="0"
    :class="classes.button"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <span
      class="flex items-center o-body justify-center"
      :class="classes.body"
    >
      <span v-if="iconLeft" class="pr-1">
        <OSvg class="w-2 fill-current icon-left" :svg="iconLeft" />
      </span>
      <span>
        <slot />
      </span>
      <span v-if="iconRight" class="pl-1">
        <OSvg class="w-2 fill-current icon-right" :svg="iconRight" />
      </span>
    </span>
  </component>
</template>

<script>
import OSvg from '@/components/OSvg'

export default {
  name: 'NewButton',
  components: { OSvg },
  props: {
    tag: {
      type: String,
      default: 'button',
      validator: value => ['button', 'a', 'nuxt-link'].includes(value)
    },
    type: {
      type: String,
      default: null,
      validator: value => ['button', 'submit'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    to: {
      type: [String, Object],
      default: null
    },
    href: {
      type: String,
      default: ''
    },
    iconRight: {
      type: String,
      default: null
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconClasses: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'solid',
      validator: value => ['solid', 'outline', 'plane'].includes(value)
    },
    color: {
      type: String,
      default: 'yellow',
      validator: value => ['green', 'yellow', 'blue', 'white', 'red', 'women', 'black', 'women-fill', 'women-orange'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['md', 'lg'].includes(value)
    },
    attribute: {
      type: Object,
      default: null,
      validator: value => ['name', 'value'].every(prop => value.hasOwnProperty(prop))
    }
  },
  computed: {
    classes () {
      return {
        button: `new-o-button-${this.variant} new-o-button-${this.color} ${this.disabled ? 'new-o-button-disabled' : ''}`,
        body: `new-o-button-body new-o-button-${this.variant}-p new-o-button-${this.size}`
      }
    },
    buttonProps () {
      const buttonProps = {
        ...(this.attribute ? { [this.attribute.name]: this.attribute.value } : {}),
        ...(this.href ? { href: this.href } : {}),
        ...(this.to ? { to: this.to } : {})
      }

      return buttonProps
    },
    componentType () {
      if (this.type) {
        return this.type
      }

      if (this.tag !== 'button') {
        return undefined
      }

      return 'button'
    }
  }
}
</script>

<style lang="postcss" scoped>
.new-o-button {
  @apply inline-block;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  outline: none !important;
  border-radius: 10rem;
  position: relative;

  &:focus {
    outline: none !important;
  }

  .icon-left, .icon-right {
    padding-top: .2rem;
  }

  &-body {
    @apply px-5;
    padding-top: .95rem;
    padding-bottom: .95rem;

    &.new-o-button-lg {
      @apply py-3 px-2;
    }
  }

  &-plane-p {
    @apply px-0;
  }

  &-solid {
    @apply font-bold transition-colors ease-in-out duration-300;

    &.new-o-button-disabled {
      @apply opacity-50 cursor-default;
    }

    &.new-o-button-blue {
      @apply bg-newBlue-400 text-neutral-00;

      &:hover {
        @apply bg-newBlue-600;
      }
    }

    &.new-o-button-yellow {
      @apply bg-yellow-400 text-neutral-800;

      &:hover {
        @apply bg-yellow-600;
      }

      span {
        @apply font-bold;
      }
    }

    &.new-o-button-white {
      @apply bg-neutral-00 text-neutral-800;

      &:hover {
        @apply bg-neutral-50;
      }
    }

    &.new-o-button-red {
      @apply bg-newRed-400 text-neutral-00;

      &:hover {
        @apply bg-newRed-600;
      }
    }

    &.new-o-button-women {
      @apply bg-women-text text-women-shade;

      &:hover {
        @apply bg-women-textShade;
      }
    }

    &.new-o-button-green {
      @apply bg-green-400 text-neutral-00;

      &:hover {
        @apply bg-green-500;
      }
    }

    &.new-o-button-black {
      @apply bg-neutral-800 text-neutral-00;

      &:hover {
        @apply bg-black;
      }
    }

    &.new-o-button-women-fill {
      @apply bg-women-fill text-neutral-00;

      &:hover {
        @apply bg-women-fill-hover;
      }
    }

    &.new-o-button-women-orange {
      @apply text-neutral-00;
    }
  }

  &-outline {
    @apply font-semibold border-1 border-solid;

    &:hover {
      @apply bg-neutral-50;
    }

    &.new-o-button-blue {
      @apply border-newBlue-400 text-newBlue-400;

      &:hover {
        @apply bg-newBlue-600;
      }
    }
  }
}

.new-o-button:focus {
  --ring-color: #36C0B6;
  --ring-offset-width: 2px;
  --ring-offset-color: transparent;
  --ring-width: 2px;
  --ring-padding: 0px;
  box-shadow: 0 0 0 var(--ring-offset-width) var(--ring-offset-color), 0 0 0 calc(var(--ring-offset-width) + var(--ring-width) + var(--ring-padding)) var(--ring-color) !important;
}
</style>
